import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { modals } from '@mantine/modals';
import { Text } from '@mantine/core';
import { showToast } from '../utils/toast';
import { getErrorMessage } from '../utils/api';
import {
  useReviewRequests,
  useUpdateReviewRequestStatus,
  useDeleteReviewRequest,
  useRemindReviewers,
  useCreateReviewRequest,
  useSetReviewers,
} from './useApi';
import { ReviewRequest, Competency, Swimlane } from '../types';
import { useUserStore } from '../store/userStore';
import { getReviewStatusConfig } from '../utils/statusUtils';
import { RejectReasonForm } from '../components/review/RejectReasonModal';

interface UseReviewRequestsManagerProps {
  filters?: { applicant?: string; reviewer?: string; status?: string };
  /** Одна таблица всех заявок, которые отдал сервер (страница «Ревью компетенций»). */
  isAllRequests?: boolean;
}

interface UseReviewRequestsManagerReturn {
  reviewRequests: ReviewRequest[] | undefined;
  myRequests: ReviewRequest[];
  requestsToReview: ReviewRequest[];
  isLoading: boolean;
  error: Error | null;
  selectedRequest: ReviewRequest | null;
  isModalOpen: boolean;
  drawer: { comp: Competency; gradeIdx: number; swimlane: Swimlane } | null;
  getStatusConfig: (status: string) => { color: string; text: string };
  handleStatusUpdate: (
    requestId: string,
    status: 'approved' | 'rejected',
    comment?: string,
  ) => Promise<void>;
  handleReject: (requestId: string) => void;
  handleRemind: (request: ReviewRequest) => void;
  handleResubmit: (request: ReviewRequest) => void;
  handleDelete: (request: ReviewRequest) => void;
  assignRequest: ReviewRequest | null;
  isSavingReviewers: boolean;
  openAssignReviewers: (request: ReviewRequest) => void;
  closeAssignReviewers: () => void;
  handleSaveReviewers: (reviewerIds: string[]) => Promise<void>;
  handleView: (request: ReviewRequest) => void;
  handleCloseModal: () => void;
  handleOpenDrawer: (data: { comp: Competency; gradeIdx: number; swimlane: Swimlane }) => void;
  handleCloseDrawer: () => void;
}

export const useReviewRequestsManager = ({
  filters = {},
  isAllRequests = false,
}: UseReviewRequestsManagerProps = {}): UseReviewRequestsManagerReturn => {
  const { t } = useTranslation();
  const user = useUserStore((s) => s.user);
  const [selectedRequest, setSelectedRequest] = useState<ReviewRequest | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [drawer, setDrawer] = useState<{
    comp: Competency;
    gradeIdx: number;
    swimlane: Swimlane;
  } | null>(null);

  // Получаем заявки на ревью
  const { data: reviewRequests, isLoading, error } = useReviewRequests(filters);
  const updateStatusMutation = useUpdateReviewRequestStatus();
  const deleteMutation = useDeleteReviewRequest();
  const remindMutation = useRemindReviewers();
  const createMutation = useCreateReviewRequest();
  const setReviewersMutation = useSetReviewers();
  const [assignRequest, setAssignRequest] = useState<ReviewRequest | null>(null);

  // Заявка «на ревью» у человека: сервер разрешил решать (процесс или competencies.review по
  // охвату — например, PM своим людям), он назначенный ревьюер или входит в пул.
  const canReview = (request: ReviewRequest, userId?: string) =>
    !!userId &&
    (request.canDecide === true ||
      request.reviewer?._id === userId ||
      (request.reviewerPool || []).some((r) => (typeof r === 'string' ? r : r._id) === userId));

  // Фильтруем заявки в зависимости от роли пользователя
  const filteredRequests = reviewRequests
    ? isAllRequests
      ? reviewRequests // Всё, что отдал сервер по охвату
      : user
        ? reviewRequests.filter(
            (request) => request.applicant._id === user._id || canReview(request, user._id),
          )
        : []
    : [];

  // Разделяем на блоки для обычных пользователей
  const myRequests = isAllRequests
    ? []
    : filteredRequests.filter((request) => request.applicant._id === user?._id) || [];
  const requestsToReview = isAllRequests
    ? []
    : filteredRequests.filter((request) => canReview(request, user?._id)) || [];

  const getStatusConfig = (status: string) => getReviewStatusConfig(status, t);

  // Обработчики действий
  const handleStatusUpdate = async (
    requestId: string,
    status: 'approved' | 'rejected',
    comment?: string,
  ) => {
    try {
      const updated: ReviewRequest = await updateStatusMutation.mutateAsync({
        id: requestId,
        data: { status, reviewerComment: comment },
      });
      // Одобрил, но заявка ещё висит — значит ждём остальных обязательных проверяющих.
      if (status === 'approved' && updated?.status === 'pending') {
        showToast({
          title: t('messages.success'),
          message: t('reviewRequests.approvalRecordedWaiting'),
        });
      } else {
        showToast({
          title: t('messages.success'),
          message: t('reviewRequests.statusUpdated'),
        });
      }
    } catch (error) {
      const message = getErrorMessage(error, t('messages.errorUpdating'));
      showToast({ title: t('common.error'), message, color: 'red' });
    }
  };

  // Отклонение всегда с обязательным комментарием — спрашиваем причину.
  const handleReject = (requestId: string) => {
    const modalId = modals.open({
      title: t('reviewRequests.rejectReasonTitle'),
      children: (
        <RejectReasonForm
          onCancel={() => modals.close(modalId)}
          onConfirm={(reason) => {
            modals.close(modalId);
            void handleStatusUpdate(requestId, 'rejected', reason);
          }}
        />
      ),
    });
  };

  // Напомнить проверяющим о зависшей заявке.
  const handleRemind = async (request: ReviewRequest) => {
    try {
      const res = await remindMutation.mutateAsync(request._id);
      showToast({
        title: t('messages.success'),
        message: t('reviewRequests.reminded', { count: res.reminded }),
      });
    } catch (error) {
      const message = getErrorMessage(error, t('messages.errorUpdating'));
      showToast({ title: t('common.error'), message, color: 'red' });
    }
  };

  // Переотправить отклонённую заявку (создаёт новую по той же компетенции).
  const handleResubmit = (request: ReviewRequest) => {
    modals.openConfirmModal({
      title: t('reviewRequests.resubmitTitle'),
      children: <Text size="sm">{t('reviewRequests.resubmitText')}</Text>,
      labels: { confirm: t('reviewRequests.resubmit'), cancel: t('common.cancel') },
      onConfirm: async () => {
        try {
          await createMutation.mutateAsync({
            competencyId: request.competency._id,
            competencyMapId: request.competencyMap._id,
            comment: request.comment,
          });
          showToast({ title: t('messages.success'), message: t('reviewRequests.created') });
        } catch (error) {
          const message = getErrorMessage(error, t('messages.errorUpdating'));
          showToast({ title: t('common.error'), message, color: 'red' });
        }
      },
    });
  };

  // Ручное назначение проверяющих (админ).
  const openAssignReviewers = (request: ReviewRequest) => setAssignRequest(request);
  const closeAssignReviewers = () => setAssignRequest(null);
  const handleSaveReviewers = async (reviewerIds: string[]) => {
    if (!assignRequest) return;
    try {
      await setReviewersMutation.mutateAsync({ id: assignRequest._id, reviewerIds });
      showToast({ title: t('messages.success'), message: t('reviewRequests.reviewersUpdated') });
      setAssignRequest(null);
    } catch (error) {
      const message = getErrorMessage(error, t('messages.errorUpdating'));
      showToast({ title: t('common.error'), message, color: 'red' });
    }
  };

  const handleDelete = (request: ReviewRequest) => {
    modals.openConfirmModal({
      title: t('messages.confirmDelete', { item: t('reviewRequests.title') }),
      children: (
        <Text size="sm">{t('messages.confirmDelete', { item: t('reviewRequests.title') })}</Text>
      ),
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(request._id);
          showToast({
            title: t('messages.deleted', { item: t('reviewRequests.title') }),
            message: '',
          });
        } catch (error) {
          const message = getErrorMessage(
            error,
            t('messages.errorDeleting', { item: t('reviewRequests.title') }),
          );
          showToast({ title: t('common.error'), message, color: 'red' });
        }
      },
    });
  };

  const handleView = (request: ReviewRequest) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
  };

  const handleOpenDrawer = (data: { comp: Competency; gradeIdx: number; swimlane: Swimlane }) => {
    setDrawer(data);
  };

  const handleCloseDrawer = () => {
    setDrawer(null);
  };

  return {
    // Данные
    reviewRequests: filteredRequests,
    myRequests,
    requestsToReview,
    isLoading,
    error,

    // Состояние модальных окон
    selectedRequest,
    isModalOpen,
    drawer,

    // Утилиты
    getStatusConfig,

    // Обработчики
    handleStatusUpdate,
    handleReject,
    handleRemind,
    handleResubmit,
    handleDelete,
    assignRequest,
    isSavingReviewers: setReviewersMutation.isPending,
    openAssignReviewers,
    closeAssignReviewers,
    handleSaveReviewers,
    handleView,
    handleCloseModal,
    handleOpenDrawer,
    handleCloseDrawer,
  };
};
